/**
 * Сервис для загрузки анализа по external_id
 * Используется для получения анализа по URL результата
 */

import { Analysis } from '../types';

const SHARE_SERVER_URL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';

/**
 * Интерфейс для ответа API при загрузке анализа
 */
interface AnalysisApiResponse {
  id: string;
  external_id: string;
  title: string;
  description: string;
  url?: string;
  items: Array<{
    id: string;
    text: string;
    rating: number;
    category: 'plus' | 'minus';
  }>;
  created_at: string;
  recommendation: string;
  summary?: string;
  improvement_suggestions?: string;
  improved_request?: string;
  action_plan?: string;
  improvementSuggestions?: string;
  improvedRequest?: string;
  actionPlan?: string;
  expert?: string;
  person?: string;
  question?: string;
  no_question?: boolean;
  prompt_package?: string;
  promptPackage?: string;
  userRating?: 'like' | 'dislike' | null;
  category?: string | null;
  analysisMode?: 'standard' | 'evaluation' | null;
  argumentSelectionLogic?: string | null;
  plusListTitle?: string | null;
  minusListTitle?: string | null;
  seo?: {
    title?: string;
    description?: string;
    slug?: string;
  } | null;
}

/**
 * Загружает анализ по external_id с сервера
 */
export async function loadAnalysisByExternalId(externalId: string): Promise<Analysis> {
  try {
    console.log('📥 Загружаем анализ по external_id:', externalId);
    
    const response = await fetch(`${SHARE_SERVER_URL}/api/analysis/${externalId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('ANALYSIS_NOT_FOUND');
      }
      throw new Error(`HTTP ошибка: ${response.status}`);
    }

  const data: AnalysisApiResponse = await response.json();
    
    // Вычисляем исходный вердикт на основе исходных items от ИИ
    // Если originalDecision уже есть в данных, используем его, иначе вычисляем
    const { calculateVerdict } = await import('../utils/verdictCalculator');
    const originalDecision = (data as any).originalDecision || calculateVerdict(data.items.map((item: any) => ({
      id: item.id,
      text: item.text,
      rating: item.rating,
      category: item.category
    })));
    
    // Преобразуем данные из API в формат приложения
    const analysis: Analysis = {
      id: data.id,
      external_id: data.external_id,
      title: data.title,
      description: data.description,
      url: data.url,
      items: data.items.map(item => ({
        id: item.id,
        text: item.text,
        rating: item.rating,
        category: item.category,
        isModified: false, // По умолчанию не изменен
        isExpanded: false, // По умолчанию не расширен
      })),
      createdAt: new Date(data.created_at),
      recommendation: data.recommendation,
      summary: data.summary,
      improvementSuggestions: data.improvementSuggestions || data.improvement_suggestions,
      improvedRequest: data.improvedRequest || data.improved_request,
      actionPlan: data.actionPlan || data.action_plan,
      expert: data.expert,
      person: data.person,
      question: data.question,
      noQuestion: data.no_question,
      promptPackage: data.promptPackage || data.prompt_package,
      isModified: false, // По умолчанию не изменен
      userRating: data.userRating || null,
      originalDecision, // Сохраняем исходный вердикт
      category: data.category || null, // Категория анализа
      analysisMode: data.analysisMode || null,
      argumentSelectionLogic: data.argumentSelectionLogic || null,
      plusListTitle: data.plusListTitle || null,
      minusListTitle: data.minusListTitle || null,
      seo: data.seo || null, // SEO-метаданные
    };
    
    return analysis;
  } catch (error) {
    console.error('❌ Ошибка при загрузке анализа:', error);
    throw error;
  }
}

/**
 * Проверяет, существует ли анализ с указанным external_id
 */
export async function checkAnalysisExists(externalId: string): Promise<boolean> {
  try {
    const response = await fetch(`${SHARE_SERVER_URL}/api/analysis/${externalId}`, {
      method: 'HEAD', // Используем HEAD для проверки существования без загрузки данных
      credentials: 'include',
    });

    return response.ok;
  } catch (error) {
    console.error('Ошибка при проверке существования анализа:', error);
    return false;
  }
}

/**
 * Получает метаданные анализа (только основная информация без полного содержимого)
 */
export async function getAnalysisMetadata(externalId: string): Promise<{
  id: string;
  external_id: string;
  title: string;
  created_at: string;
  items_count: number;
} | null> {
  try {
    const response = await fetch(`${SHARE_SERVER_URL}/api/analysis/${externalId}/metadata`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP ошибка: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка при получении метаданных анализа:', error);
    return null;
  }
}
